import * as React from 'react'

import {
  Box,
  CollapsableCard,
  Spinner,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import { ProcessesProcess } from 'generated/resources/graphql'

import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import { useAssignableProcesses } from 'modules/processes/hooks/useAssignableProcesses'

const NOW = 'now'

export const processesOptions = (processes: ProcessesProcess[]) => {
  return processes
    .filter((process) => process.validSettings)
    .map((process) => {
      return (
        <Selector.Option
          key={process.id}
          value={process.id}
          label={process.name}
          searchValue={process.name}
        >
          {process.name}
        </Selector.Option>
      )
    })
}

interface Props {
  onWorkflowSelect: (template: string | undefined) => void
  selectedWorkflow?: string | number
  isDisabled?: boolean
  mandatory?: boolean
  onToggle?: (isOpen: boolean) => void
  error?: string
  isOpen?: boolean
  enableEmployeeOnboarding: boolean
  updateWorkFlowSettings: (enable: boolean, workflowId: string | undefined) => void
}

type SelectableSectionProps = {
  title: string
  type: string
  description: string
  selected?: boolean
  onChange: () => void
}

const Card = ({
  open,
  onChange,
  title,
  description,
}: {
  open: boolean
  title: string
  description: string
  onChange: () => void
}) => {
  return (
    <button type="button" style={{ width: '100%' }}>
      <Box textAlign="left" gap="s8">
        <Box flexDirection="row" justifyContent="spaceBetween" gap="s16" alignItems="center">
          <Box style={{ maxWidth: '90%' }} flexDirection="row" alignItems="center" gap="s8">
            <Text.Normal weight="medium" lineClamp={3} color="grey1000">
              {title}
            </Text.Normal>
          </Box>
          <TogglerCheckbox value={open} onChange={onChange} />
        </Box>
        <Box flexDirection="row" alignItems="center">
          {description && (
            <Text.Normal color="grey800" size="200" lineClamp={3}>
              {description}
            </Text.Normal>
          )}
        </Box>
      </Box>
    </button>
  )
}

const SelectableSection = ({
  type,
  title,
  description,
  onChange,
  selected,
  children,
}: React.PropsWithChildren<SelectableSectionProps>) => {
  return (
    <CollapsableCard
      open={!!selected}
      Card={<Card open={!!selected} onChange={onChange} title={title} description={description} />}
      Content={children}
    />
  )
}

const WorkflowSelector = ({
  selectedWorkflow,
  isDisabled,
  mandatory,
  onToggle,
  error,
  isOpen,
  onWorkflowSelect,
  enableEmployeeOnboarding,
  updateWorkFlowSettings,
}: Props) => {
  const { loading, processes } = useAssignableProcesses(['onboarding'])

  const onChange = () => {
    // unset the workflow when toggling enableEmployeeOnboarding
    updateWorkFlowSettings(!enableEmployeeOnboarding, undefined)
  }

  if (loading)
    return (
      <Box flexGrow justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Box gap="s6">
      <Text.Normal weight="medium" color="grey1000">
        {i18n.t('employee_form.wizard.onboarding_step.onboarding_workflow_selector.label')}
      </Text.Normal>
      <Box gap="s16">
        <SelectableSection
          type={NOW}
          selected={enableEmployeeOnboarding}
          onChange={onChange}
          description={i18n.t(
            'employee_form.wizard.onboarding_step.onboarding_workflow_selector.description'
          )}
          title={i18n.t('employee_form.wizard.onboarding_step.onboarding_workflow_selector.title')}
        >
          <Selector.Popover
            label={i18n.t(
              'employee_form.wizard.onboarding_step.onboarding_workflow_selector.selector_label'
            )}
            blankLabel=""
            isOpen={isOpen}
            onToggle={onToggle}
            searchPlaceholder=""
            placeholder={i18n.t(
              'employee_form.wizard.onboarding_step.onboarding_workflow_selector.placeholder'
            )}
            onChange={(value) => onWorkflowSelect(value ? `${value}` : undefined)}
            value={selectedWorkflow ? [+selectedWorkflow] : []}
            isDisabled={isDisabled}
            mandatory={mandatory}
            error={error}
          >
            {processesOptions(processes)}
          </Selector.Popover>
        </SelectableSection>
      </Box>
    </Box>
  )
}

export default WorkflowSelector
